const LICHESS_ORIGIN = 'https://lichess.org';

/** Paths Lichess does not expose with CORS (browser must use same-origin proxy). */
const PROXY_PATH_PREFIXES = ['/game/export/'];

export type LichessAccount = {
  id: string;
  username: string;
};

export type LichessPlayerSide = {
  user?: { id?: string; name?: string };
  aiLevel?: number;
  name?: string;
};

export type LichessGame = {
  id: string;
  createdAt: number;
  lastMoveAt?: number;
  speed?: string;
  perf?: string;
  status?: string;
  winner?: 'white' | 'black';
  players: {
    white: LichessPlayerSide;
    black: LichessPlayerSide;
  };
  /** Present when fetched with pgnInJson=true (CORS-safe games list). */
  pgn?: string;
};

function resolveLichessUrl(path: string): string {
  if (path.startsWith('http')) return path;
  const needsProxy = PROXY_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
  if (needsProxy) return `/api/lichess${path}`;
  return `${LICHESS_ORIGIN}${path}`;
}

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token.trim()}` };
}

async function lichessFetch(path: string, token: string, init?: RequestInit): Promise<Response> {
  const url = resolveLichessUrl(path);
  const res = await fetch(url, {
    ...init,
    headers: {
      ...authHeaders(token),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      body.trim() || `Lichess API ${res.status}${res.status === 401 ? ' — check your token' : ''}`
    );
  }
  return res;
}

export async function fetchLichessAccount(token: string): Promise<LichessAccount> {
  const res = await lichessFetch('/api/account', token);
  const data = (await res.json()) as { id?: string; username?: string };
  const id = data.id?.trim();
  const username = data.username?.trim();
  if (!id || !username) throw new Error('Lichess account response missing id or username.');
  return { id, username };
}

/** Recent games as NDJSON (newest first), including full PGN per game. */
export async function fetchRecentLichessGames(
  token: string,
  username: string,
  max = 10
): Promise<LichessGame[]> {
  const path = `/api/games/user/${encodeURIComponent(username)}?max=${max}&pgnInJson=true&tags=true&moves=true`;
  const res = await lichessFetch(path, token, {
    headers: { Accept: 'application/x-ndjson' },
  });
  const text = await res.text();
  const games: LichessGame[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      games.push(JSON.parse(trimmed) as LichessGame);
    } catch {
      /* skip malformed line */
    }
  }
  return games;
}

/** Full PGN for one game (via same-origin proxy — direct /game/export is blocked by CORS). */
export async function fetchLichessGamePgn(token: string, gameId: string): Promise<string> {
  const res = await lichessFetch(`/game/export/${encodeURIComponent(gameId)}`, token, {
    headers: { Accept: 'application/x-chess-pgn' },
  });
  const pgn = (await res.text()).trim();
  if (!pgn) throw new Error('Empty PGN from Lichess.');
  return pgn;
}

/** Prefer PGN embedded in the games list; avoids an extra export request. */
export function pgnFromLichessGame(game: LichessGame): string {
  return game.pgn?.trim() ?? '';
}
